// utils/API/BackendHelper/admin/exam.js
import ApiHelper from '../../ApiHelper';
import API_ENDPOINTS from '../../endpoints';

class AdminExamHelper {
  /**
   * Get all exams with optional filters
   * @param {Object} params - Query parameters (course_id, from_date, to_date)
   * @returns {Promise} List of exams with statistics
   */
  async getAllExams(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = API_ENDPOINTS.ADMIN.EXAMS.BASE + (queryParams ? `?${queryParams}` : '');
      const response = await ApiHelper.get(url);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch exams: ${error.message}`);
    }
  }

  /**
   * Get exam details by ID
   * @param {string} examId - The exam ID
   * @returns {Promise} Exam details with questions
   */
  async getExamById(examId) {
    try {
      const response = await ApiHelper.get(API_ENDPOINTS.ADMIN.EXAMS.BY_ID(examId));
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch exam details: ${error.message}`);
    }
  }

  /**
   * Create a new exam
   * @param {Object} examData - Exam data including questions
   * @returns {Promise} Created exam data
   */
  async createExam(examData) {
    try {
      const response = await ApiHelper.post(API_ENDPOINTS.ADMIN.EXAMS.CREATE, examData);
      return response;
    } catch (error) {
      throw new Error(`Failed to create exam: ${error.message}`);
    }
  }

  /**
   * Update an existing exam
   * @param {string} examId - The exam ID
   * @param {Object} updateData - Updated exam data
   * @returns {Promise} Updated exam data
   */
  async updateExam(examId, updateData) {
    try {
      const response = await ApiHelper.put(API_ENDPOINTS.ADMIN.EXAMS.UPDATE(examId), updateData);
      return response;
    } catch (error) {
      throw new Error(`Failed to update exam: ${error.message}`);
    }
  }

  /**
   * Delete an exam
   * @param {string} examId - The exam ID
   * @returns {Promise} Deletion confirmation
   */
  async deleteExam(examId) {
    try {
      const response = await ApiHelper.delete(API_ENDPOINTS.ADMIN.EXAMS.DELETE(examId));
      return response;
    } catch (error) {
      throw new Error(`Failed to delete exam: ${error.message}`);
    }
  }

  /**
   * Toggle exam active status
   * @param {string} examId - The exam ID
   * @param {boolean} isActive - Active status
   * @returns {Promise} Updated exam data
   */
  async toggleExamStatus(examId, isActive) {
    try {
      const response = await ApiHelper.patch(
        API_ENDPOINTS.ADMIN.EXAMS.TOGGLE_STATUS(examId),
        { is_active: isActive }
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to toggle exam status: ${error.message}`);
    }
  }

  /**
   * Declare or undeclare exam results
   * @param {string} examId - The exam ID
   * @param {boolean} resultsDeclared - Results declaration status
   * @returns {Promise} Updated exam data
   */
  async declareExamResults(examId, resultsDeclared) {
    try {
      const response = await ApiHelper.patch(
        API_ENDPOINTS.ADMIN.EXAMS.DECLARE_RESULTS(examId),
        { results_declared: resultsDeclared }
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to declare exam results: ${error.message}`);
    }
  }

  /**
   * Get exam attempts and analytics
   * @param {string} examId - The exam ID
   * @returns {Promise} Exam attempts with statistics
   */
  async getExamAttempts(examId) {
    try {
      const response = await ApiHelper.get(API_ENDPOINTS.ADMIN.EXAMS.ATTEMPTS(examId));
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch exam attempts: ${error.message}`);
    }
  }

  /**
   * Get live analytics for an exam (eligible cohort, progress breakdown, score stats)
   * @param {string} examId - The exam ID
   * @returns {Promise} Exam analytics with summary and per-status student lists
   */
  async getExamAnalytics(examId) {
    try {
      const response = await ApiHelper.get(API_ENDPOINTS.ADMIN.EXAMS.ANALYTICS(examId));
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch exam analytics: ${error.message}`);
    }
  }

  /**
   * Add questions to an exam
   * @param {string} examId - The exam ID
   * @param {Array} questions - Array of question objects
   * @returns {Promise} Created questions
   */
  async addExamQuestions(examId, questions) {
    try {
      const response = await ApiHelper.post(
        API_ENDPOINTS.ADMIN.EXAMS.QUESTIONS(examId),
        { questions }
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to add exam questions: ${error.message}`);
    }
  }

  /**
   * Update exam questions
   * @param {string} examId - The exam ID
   * @param {Array} questions - Updated questions array
   * @returns {Promise} Updated questions
   */
  async updateExamQuestions(examId, questions) {
    try {
      const response = await ApiHelper.put(
        API_ENDPOINTS.ADMIN.EXAMS.QUESTIONS(examId),
        { questions }
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to update exam questions: ${error.message}`);
    }
  }
}

const adminExamHelper = new AdminExamHelper();
export default adminExamHelper;